import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Settings2, Search, FileText, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, peso, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { NeedTenant } from "../components/layout/AppShell";

const EMPTY = { last_name: "", first_name: "", middle_initial: "", address: "", age: "", contact_number: "", height: "", weight: "", tags: "", notes: "" };

export default function CustomersPage() {
  const { user, activeTenantId } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [labels, setLabels] = useState({ module_label: "Customers", record_label: "Customer" });
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState({ module_label: "", record_label: "" });
  const [selected, setSelected] = useState(null); // customer_id
  const [history, setHistory] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const canEdit = ["owner", "manager", "system_admin"].includes(user.role);

  const doDelete = async () => {
    try {
      await api.delete(`/customers/${deleteTarget.customer_id}`);
      toast.success("Record deleted");
      setDeleteTarget(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: l }] = await Promise.all([
        api.get("/customers"), api.get("/tenant/labels"),
      ]);
      setCustomers(c);
      setLabels(l);
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, activeTenantId]);

  const filtered = customers.filter((c) => {
    const q = search.trim().toLowerCase();
    return !q || `${c.last_name} ${c.first_name}`.toLowerCase().includes(q) || (c.contact_number || "").includes(q);
  });

  const openAdd = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(c).map(([k, v]) => [k, v ?? ""])), age: c.age ?? "" });
    setFormOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, age: form.age === "" ? null : Number(form.age) };
    try {
      if (editing) await api.put(`/customers/${editing.customer_id}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Record updated" : `${labels.record_label} added`);
      setFormOpen(false);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const saveLabels = async () => {
    try {
      const { data } = await api.put("/tenant/labels", labelDraft);
      setLabels(data);
      setLabelsOpen(false);
      toast.success("Labels updated");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const openRecord = async (c) => {
    setSelected(c.customer_id);
    setHistory(null);
    try {
      const { data } = await api.get(`/customers/${c.customer_id}/history`);
      setHistory(data);
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/customers/${selected}/notes`, { note: noteText });
      setNoteText("");
      const { data } = await api.get(`/customers/${selected}/history`);
      setHistory(data);
      toast.success("Note added");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{labels.module_label}</h1>
          <p className="text-sm text-slate-500">{customers.length} records</p>
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${labels.module_label.toLowerCase()}…`} className="pl-9 w-64 bg-white" data-testid="client-search-input" />
        </div>
        {canEdit && (
          <Button variant="outline" onClick={() => { setLabelDraft(labels); setLabelsOpen(true); }} data-testid="client-labels-settings-btn">
            <Settings2 className="h-4 w-4 mr-1.5" /> Labels
          </Button>
        )}
        <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="client-add-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add {labels.record_label}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="clients-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Age</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Height / Weight</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.customer_id} data-testid={`client-row-${c.customer_id}`}>
                <TableCell>
                  <p className="font-medium text-slate-900">{c.last_name}, {c.first_name} {c.middle_initial || ""}</p>
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-600">{c.contact_number || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{c.age ?? "—"}</TableCell>
                <TableCell className="text-sm text-slate-600 max-w-40 truncate">{c.address || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{[c.height, c.weight].filter(Boolean).join(" / ") || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{c.tags || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="View record & history" onClick={() => openRecord(c)} data-testid={`view-client-${c.customer_id}`}>
                    <FileText className="h-4 w-4 text-slate-500" />
                  </Button>
                  {canEdit && (
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-client-${c.customer_id}`}>
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                  )}
                  {user.role === "system_admin" && (
                    <Button variant="ghost" size="icon" title="Delete (admin)" onClick={() => setDeleteTarget(c)} data-testid={`delete-client-${c.customer_id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">No records yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="client-form-modal">
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit ${labels.record_label}` : `Add ${labels.record_label}`}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Last name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="client-last-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>First name</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="client-first-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>M.I.</Label>
                <Input value={form.middle_initial} onChange={(e) => setForm({ ...form, middle_initial: e.target.value })} maxLength={2} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Age</Label>
                <Input type="number" min="0" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Contact number</Label>
                <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} data-testid="client-contact-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Height</Label>
                <Input value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} placeholder="e.g. 165 cm" />
              </div>
              <div className="space-y-1.5">
                <Label>Weight</Label>
                <Input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="e.g. 60 kg" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Tags</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="VIP, senior, member…" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <Button type="submit" className="w-full bg-slate-900" data-testid="client-save-btn">{editing ? "Save changes" : "Add record"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-xs" data-testid="client-delete-dialog">
          <DialogHeader><DialogTitle className="font-heading">Delete record?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {deleteTarget?.first_name} {deleteTarget?.last_name} will be permanently deleted. Their notes history is removed with the record; past invoices are kept.
          </p>
          <Button className="w-full bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="client-delete-confirm-btn">Delete permanently</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="client-record-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {history ? `${history.customer.last_name}, ${history.customer.first_name} ${history.customer.middle_initial || ""}` : "Loading…"}
            </DialogTitle>
          </DialogHeader>
          {history && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                <p>Contact: <span className="font-mono">{history.customer.contact_number || "—"}</span></p>
                <p>Age: {history.customer.age ?? "—"}</p>
                <p>Height/Weight: {[history.customer.height, history.customer.weight].filter(Boolean).join(" / ") || "—"}</p>
                <p className="col-span-2">Address: {history.customer.address || "—"}</p>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <StickyNote className="h-3.5 w-3.5" /> Notes &amp; Remarks ({history.notes.length})
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {[...history.notes].reverse().map((n, i) => (
                    <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                      <p className="text-sm text-slate-700">{n.note}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{n.by} · {fmtDateTime(n.at)}</p>
                    </div>
                  ))}
                  {history.notes.length === 0 && <p className="text-xs text-slate-400">No notes yet.</p>}
                </div>
                <div className="flex gap-2">
                  <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder={`Add a note for this ${labels.record_label.toLowerCase()}…`}
                    onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="client-note-input" />
                  <Button className="bg-slate-900 shrink-0" onClick={addNote} data-testid="client-add-note-btn">Add note</Button>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Transaction History ({history.transactions.length})</h4>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {history.transactions.map((t) => (
                    <div key={t.sale_id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`client-txn-${t.sale_id}`}>
                      <div>
                        <p className="font-mono text-xs font-semibold text-slate-800">{t.invoice_number}</p>
                        <p className="text-[11px] text-slate-500">{t.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
                        <p className="text-[10px] text-slate-400">{fmtDateTime(t.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm font-semibold">{peso(t.total_amount)}</p>
                        <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${t.status === "PAID" ? "bg-emerald-100 text-emerald-700" : t.status === "VOID" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}`}>{t.status}</span>
                      </div>
                    </div>
                  ))}
                  {history.transactions.length === 0 && <p className="text-xs text-slate-400">No transactions linked to this record yet.</p>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={labelsOpen} onOpenChange={setLabelsOpen}>
        <DialogContent className="max-w-xs" data-testid="client-labels-modal">
          <DialogHeader><DialogTitle className="font-heading">Module Labels</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Rename this module to fit your business (e.g. Patients, VIP Members, Guests).</p>
            <div className="space-y-1.5">
              <Label>Module label (plural)</Label>
              <Input value={labelDraft.module_label} onChange={(e) => setLabelDraft({ ...labelDraft, module_label: e.target.value })} data-testid="module-label-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Record label (singular)</Label>
              <Input value={labelDraft.record_label} onChange={(e) => setLabelDraft({ ...labelDraft, record_label: e.target.value })} data-testid="record-label-input" />
            </div>
            <Button className="w-full bg-slate-900" onClick={saveLabels} data-testid="labels-save-btn">Save labels</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
